import { ComponentType, FocusEvent, useCallback, useEffect, useState } from 'react';
import useKey from 'react-use/lib/useKey';
import { IconSearch } from '../icon/IconSearch';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { Typography } from '../typography/Typography';
import { Suggest } from './Suggest/Suggest';
import { ControlProps } from './makeTextControl';

const OFFSET_SCROLL = 50;

export interface ControlPropsWithSuggest<TElement> extends ControlProps<TElement> {
    suggest?: {
        text: string;
        icon?: string;
    }[];
    onApply: (text?: string) => void;
    сontainerRef?: React.RefObject<HTMLDivElement>;
}

export function makeTextSearchSuggest<TElement extends HTMLInputElement>(
    Component: ComponentType<ControlProps<TElement>>,
) {
    return (props: ControlPropsWithSuggest<TElement>) => {
        const {
            suggest,
            сontainerRef,
            value,
            onApply,
            onFocus: onFocusBase,
            onBlur: onBlurBase,
        } = props;

        const itemsSuggest = suggest?.length
            ? suggest.reduce(
                  (res, item, idx) => ({
                      ...res,
                      [idx]: item.text,
                  }),
                  {},
              )
            : undefined;

        const [visibleSuggest, setVisibleSuggest] = useState(false);

        const onClose = useCallback(() => {
            setVisibleSuggest(false);
        }, []);

        const onBlurClose = useCallback(() => {
            const activeElement = document.activeElement as HTMLInputElement;

            if (activeElement) {
                activeElement.blur();
            }

            onClose();
        }, [onClose]);

        const onFocus = useCallback(
            (event: FocusEvent<TElement>) => {
                setVisibleSuggest(true);
                onFocusBase?.(event);
            },
            [onFocusBase],
        );
        const onBlur = useCallback(
            (event: FocusEvent<TElement>) => {
                setTimeout(() => onClose(), 200);

                onBlurBase?.(event);
            },
            [onBlurBase, onClose],
        );

        const onSelectItem = useCallback(
            (key: string) => {
                const item = suggest?.[Number(key)];
                onApply(item?.text || '');
            },
            [suggest, onApply],
        );

        useKey('Escape', onBlurClose, {}, [onBlurClose]);

        const onPressEnter = useCallback(() => {
            onApply(String(value));

            onBlurClose();
        }, [onApply, onBlurClose, value]);

        useKey('Enter', onPressEnter, {}, [onPressEnter]);

        const onScroll = useCallback(
            (event: Event) => {
                const scrollElem = event.target as HTMLElement;
                const scrollTop = scrollElem?.scrollTop;

                if (scrollTop > OFFSET_SCROLL) {
                    onBlurClose();
                }
            },
            [onBlurClose],
        );

        const formatSuggestText = useCallback(
            (key: string) => {
                const item = suggest?.[Number(key)];

                return item ? (
                    <Flex direction="row" alignItems="center" spacing={2}>
                        <Flex
                            width="48px"
                            height="48px"
                            background={
                                item.icon
                                    ? `url(${item.icon})`
                                    : 'var(--control-secondary-background)'
                            }
                            border="1px solid var(--background-secondary)"
                            backgroundSize="cover"
                            borderRadius={1}
                            flexShrink={0}
                            alignItems="center"
                            justifyContent="center"
                        >
                            {item.icon ? null : <IconSearch width="24px" height="24px" />}
                        </Flex>
                        <Typography variant="body">{item.text}</Typography>
                    </Flex>
                ) : (
                    ''
                );
            },
            [suggest],
        );

        useEffect(() => {
            const containerElem = сontainerRef?.current;

            if (!containerElem) {
                return;
            }

            containerElem.addEventListener('scroll', onScroll);

            return () => {
                containerElem.removeEventListener('scroll', onScroll);
            };
        }, [onScroll, сontainerRef]);

        return (
            <Box position="relative" width="100%">
                <Component {...props} onFocus={onFocus} onBlur={onBlur} />
                {itemsSuggest && value ? (
                    <Suggest
                        visible={visibleSuggest}
                        items={itemsSuggest}
                        onClick={onSelectItem}
                        trigger={<Box />}
                        onClose={onClose}
                        formatText={formatSuggestText}
                        isFullWidth
                    />
                ) : null}
            </Box>
        );
    };
}
